import React from 'react';
import { getAdminContractDisplayStatusLabel } from '../../../utils/contractStatus';

// EventDetails.js のコンポーネント外トップレベル部品を分離（挙動不変・純粋移動）。
// 状態非依存の表示ヘルパ/小コンポーネントのみ。

export function labelStatus(status, row = null) {
  const resolved = getAdminContractDisplayStatusLabel(
    row ? { ...row, status } : status
  );
  return resolved || '—';
}

export function TicketLineChart({ points = [] }) {
  if (!points.length) return <p className="muted">実績データがありません。</p>;

  const viewW = 640;
  const viewH = 240;
  const pad = 36;
  const maxVal = Math.max(...points.map((p) => p.value), 1);
  const scaleX = (viewW - pad * 2) / Math.max(1, points.length - 1);
  const scaleY = (viewH - pad * 2) / maxVal;

  const getX = (i) => pad + i * scaleX;
  const getY = (v) => viewH - pad - v * scaleY;

   // X軸ラベルの間引き（多い場合は最大10個程度に抑える）
  const maxLabels = 10;
  const labelStep =
    points.length > maxLabels ? Math.ceil(points.length / maxLabels) : 1;

  const d = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(p.value)}`)
    .join(' ');

  return (
    <div className="ts-linechart-wrap">
      <svg className="ts-linechart" viewBox={`0 0 ${viewW} ${viewH}`} role="img" aria-label="券売推移">
        <rect x="0" y="0" width={viewW} height={viewH} fill="var(--slate-50)" />
        <path d={`${d}`} className="ts-linechart-line" />
        <path d={`${d} L ${getX(points.length - 1)} ${viewH - pad} L ${getX(0)} ${viewH - pad} Z`} className="ts-linechart-area" />
        {points.map((p, i) => (
          <circle key={i} cx={getX(i)} cy={getY(p.value)} r="2" className="ts-linechart-point" />
        ))}
        {points.map((p, i) => {
          const isFirst = i === 0;
          const isLast = i === points.length - 1;
          const show = isFirst || isLast || i % labelStep === 0;
          if (!show) return null;
          return (
            <text
              key={`x-${i}`}
              x={getX(i)}
              y={viewH - pad + 16}
              className="ts-linechart-xlabel"
            >
              {p.label}
            </text>
          );
        })}
        {[0, 0.5, 1].map((t, idx) => {
          const yVal = Math.round(maxVal * t);
          const y = getY(yVal);
          return (
            <g key={idx}>
              <line x1={pad} y1={y} x2={viewW - pad} y2={y} className="ts-linechart-grid" />
              <text x={pad - 6} y={y} className="ts-linechart-ylabel">
                {yVal}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ジャンルバッジレンダー（スコープ外関数に分離）
export function renderGenreBadges(maybeGenre, genres, parsePgIntArrayFn) {
  const genresById = new Map();
  const genresByValueLower = new Map();
  for (const g of genres) {
    genresById.set(Number(g.id), g);
    if (g?.value) genresByValueLower.set(String(g.value).toLowerCase(), g);
  }

  const result = [];
  const pushUnique = (g) => {
    if (!g) return;
    const id = Number(g.id);
    if (!result.some((x) => Number(x.id) === id)) result.push(g);
  };
  const handleEl = (el) => {
    if (Number.isInteger(el) || /^\d+$/.test(String(el))) {
      pushUnique(genresById.get(Number(el)));
    } else if (typeof el === 'string') {
      pushUnique(genresByValueLower.get(el.toLowerCase()));
    }
  };

  if (Array.isArray(maybeGenre)) {
    maybeGenre.forEach(handleEl);
  } else if (typeof maybeGenre === 'string') {
    const t = maybeGenre.trim();
    if (t.startsWith('{')) {
      (parsePgIntArrayFn(t) || []).forEach((id) => pushUnique(genresById.get(Number(id))));
    } else if (t.startsWith('[')) {
      try {
        const arr = JSON.parse(t);
        if (Array.isArray(arr)) arr.forEach(handleEl);
      } catch {}
    } else if (/^\d+$/.test(t)) {
      pushUnique(genresById.get(Number(t)));
    } else {
      pushUnique(genresByValueLower.get(t.toLowerCase()));
    }
  } else if (typeof maybeGenre === 'number') {
    pushUnique(genresById.get(Number(maybeGenre)));
  }

  if (!result.length) return <span>未設定</span>;
  return (
    <div className="event-detail-genre-badges" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
      {result.map((g) => (
        <span
          key={g.id ?? g.value}
          className="event-detail-genre-badge"
          style={{
            backgroundColor: g.color || 'var(--contract-detail-action-blue)',
            color: g.text_color || '#fff',
          }}
          title={g.value}
        >
          {g.label}
        </span>
      ))}
    </div>
  );
}
